import type {
  ComputedJavaScriptVariableState,
  SetVariableObjectTypePayload,
} from '@unifyapps/defs/types/javascriptVariable';
import isPlainObject from 'lodash/isPlainObject';
import _isUndefined from 'lodash/isUndefined';
import omit from 'lodash/omit';

/**
 * Any change here should be reflected in the METHODS_DOCUMENTATION.md file
 */
export function getNextPageVariableStateForObject({
  variableState,
  operationDetails,
  onError,
}: {
  variableState: ComputedJavaScriptVariableState<unknown>;
  operationDetails: {
    operation?: SetVariableObjectTypePayload['operationDetails']['operation'];
    value?: unknown;
  };
  onError?: (message: string) => void;
}) {
  // a bad payload is logged to the dev console and surfaced to Pulse (via onError) as an error,
  // then the variable is left unchanged. Only the invalid-payload branches call this.
  const reportPayloadError = (message: string) => {
    console.error(message);
    onError?.(message);
  };
  switch (operationDetails.operation) {
    case 'SET': {
      // SET allows undefined to reset the variable, or a plain object to set it
      if (!_isUndefined(operationDetails.value) && !isPlainObject(operationDetails.value)) {
        reportPayloadError(
          'operationDetails.value should be a plain object or undefined for set operation',
        );
        return variableState;
      }
      return {
        ...variableState,
        value: operationDetails.value,
      };
    }

    case 'MERGE': {
      // validate current state is object or undefined (for initialization)
      if (!_isUndefined(variableState.value) && !isPlainObject(variableState.value)) {
        reportPayloadError(
          'variableState.value should be a plain object or undefined for merge operation',
        );
        return variableState;
      }
      // validate input value is an object (not null/array)
      if (!_isUndefined(operationDetails.value) && !isPlainObject(operationDetails.value)) {
        reportPayloadError(
          'operationDetails.value should be a plain object or undefined for merge operation',
        );
        return variableState;
      }

      return {
        ...variableState,
        value: {
          ...(variableState.value as Record<string, unknown>),
          ...(operationDetails.value as Record<string, unknown>),
        },
      };
    }
    case 'REMOVE_PROPERTY': {
      // validate current state is object or undefined (for initialization)
      if (!_isUndefined(variableState.value) && !isPlainObject(variableState.value)) {
        reportPayloadError(
          'variableState.value should be a plain object or undefined for remove property operation',
        );
        return variableState;
      }
      // validate input value is a string or array of strings (keys to be removed)
      if (
        _isUndefined(operationDetails.value) ||
        (typeof operationDetails.value !== 'string' && !Array.isArray(operationDetails.value))
      ) {
        reportPayloadError(
          'operationDetails.value is required and should be a string or array of strings for remove property operation',
        );
        return variableState;
      }
      const keysToRemove = Array.isArray(operationDetails.value)
        ? operationDetails.value
        : [operationDetails.value as string];
      return {
        ...variableState,
        value: omit(variableState.value as Record<string, unknown>, keysToRemove),
      };
    }
    default:
      return variableState;
  }
}
